import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { AuthStore, AuthStoreState, User, AuthTokens } from '@mellify/shared';
import { createEncryptedStorage } from '../lib/storage';

/**
 * Storage key used for the auth store persistence.
 * Also used by the cross-tab sync listener to detect relevant changes.
 */
export const AUTH_STORAGE_KEY = 'mellify-auth';

/**
 * Initial auth state (unauthenticated).
 */
const initialState: AuthStoreState = {
  user: null,
  tokens: null,
  isAuthenticated: false,
  isLoading: true, // Start as loading to check persisted state
  error: null,
};

/**
 * Auth Store
 *
 * Manages authentication state including user data, tokens, and auth status.
 * Uses encrypted localStorage persistence to securely store auth tokens
 * across browser sessions.
 *
 * Features:
 * - Encrypted persistence via localStorage
 * - Server-side logout with token blacklisting
 * - Cross-tab session synchronization via storage events
 * - Automatic session validation on app load
 *
 * @example
 * ```ts
 * // In a component
 * const { user, isAuthenticated, login, logout } = useAuthStore();
 *
 * // With selector for optimized re-renders
 * const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
 * ```
 */
export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      login: (user: User, tokens: AuthTokens) => {
        set({
          user,
          tokens,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
      },

      logout: () => {
        // Attempt server-side logout (fire-and-forget, don't block UI)
        const { tokens } = get();
        if (tokens?.accessToken) {
          // Dynamic import to avoid circular dependency
          import('../services/authService').then(({ authService }) => {
            authService
              .logout({ refreshToken: tokens.refreshToken || undefined })
              .catch(() => {
                // Silently ignore — client-side cleanup is sufficient
              });
          }).catch(() => {
            // Ignore import errors
          });
        }

        set({
          ...initialState,
          isLoading: false,
        });
      },

      setUser: (user: User | null) => {
        set({
          user,
          isAuthenticated: user !== null && get().tokens !== null,
        });
      },

      setTokens: (tokens: AuthTokens | null) => {
        set({
          tokens,
          isAuthenticated: tokens !== null && get().user !== null,
        });
      },

      setLoading: (isLoading: boolean) => {
        set({ isLoading });
      },

      setError: (error: string | null) => {
        set({ error });
      },

      updateUser: (updates: Partial<User>) => {
        const currentUser = get().user;
        if (currentUser) {
          set({
            user: { ...currentUser, ...updates },
          });
        }
      },
    }),
    {
      name: AUTH_STORAGE_KEY,
      storage: createJSONStorage(() => createEncryptedStorage()),
      partialize: (state) => ({
        user: state.user,
        tokens: state.tokens,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => {
        return (state, error) => {
          if (error) {
            console.warn('[AuthStore] Failed to rehydrate:', error);
          }
          // After rehydration, set loading to false
          if (state) {
            state.isLoading = false;
          }
        };
      },
    }
  )
);